/**
 * "Who's coming" ("Accept your offer" on an admission): for each child, yes or
 * no and a program; any new brother or sister; one payment plan; and, when the
 * checklist asks, whether the family is seeking financial aid.
 *
 * Answers save as the guardian goes (see useStepAnswers). A finished step
 * reads back what was answered; while the family can still submit it again
 * (until the tuition agreement is signed) it offers "Change your answers",
 * and a link that asks for it (?change=1) opens it ready to change.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { RadioGroup } from '~/components/ui/radio-group';
import { gradeLabel } from '../shared';
import type { ChecklistStepView, WhoContext } from '../useChecklistStep';
import { CHANGE_PARAM, Choice, Field, Section, selectClass, StepFooter, StepHeader, type StepProps } from './stepParts';
import { useStepAnswers } from './useStepAnswers';
import {
  answerWords, comingWords, NEW_CHILD, newChildName, optionName, programLabel, readOptions, readWhoAnswers,
  type ChildAnswer, type NewChildAnswer, type PriceOption, type WhoAnswers,
} from './whoAnswers';

type Child = WhoContext['children'][number];

export default function WhoStep({ step, readOnly, viewingAs = false, overviewPath, stepPath }: StepProps) {
  const who = step.who;
  const programs = useMemo(() => readOptions(who?.programs), [who?.programs]);
  const plans = useMemo(() => readOptions(who?.plans), [who?.plans]);
  const [searchParams] = useSearchParams();
  // Done, and the server still takes it again: the family may change it.
  const changeable = readOnly && !viewingAs && step.item.state === 'DONE' && who?.canChange === true;
  const [changing, setChanging] = useState(() => changeable && searchParams.get(CHANGE_PARAM) === '1');
  if (!who) return null;

  const header = <StepHeader step={step} />;

  return readOnly && !(changeable && changing) ? (
    <div className="space-y-6">
      {header}
      <WhoSummary who={who} answers={readWhoAnswers(step.answers)} programs={programs} plans={plans} />
      {changeable ? (
        <Button type="button" variant="outline" size="lg" className="min-h-11 w-full md:w-auto" onClick={() => setChanging(true)}>
          Change your answers
        </Button>
      ) : null}
    </div>
  ) : (
    <div className="space-y-6">
      {header}
      <WhoForm step={step} who={who} programs={programs} plans={plans} overviewPath={overviewPath} stepPath={stepPath} />
    </div>
  );
}

function WhoForm({ step, who, programs, plans, overviewPath, stepPath }: {
  step: ChecklistStepView;
  who: WhoContext;
  programs: PriceOption[];
  plans: PriceOption[];
  overviewPath: string;
  stepPath: (itemId: string) => string;
}) {
  const [initial] = useState(() => {
    const read = readWhoAnswers(step.answers);
    // Children added before the school stopped taking them here can't be
    // submitted, and the page has nowhere to remove them, so it doesn't send them.
    return who.allowNewChildren || read.newChildren.length === 0
      ? { answers: read, dropped: false }
      : { answers: { ...read, newChildren: [] }, dropped: true };
  });
  // The dropped children go with the first save, made by a change or Submit.
  const { answers, change, submit: handleSubmit, saveState, errors, submitting } = useStepAnswers<WhoAnswers>({
    itemId: step.item.id,
    initial: initial.answers,
    overviewPath,
    stepPath,
    unsentAtStart: initial.dropped,
  });

  const changeChild = (id: string, answer: Partial<ChildAnswer>) => change((current) => ({
    ...current,
    children: {
      ...current.children,
      [id]: { ...(current.children[id] ?? { coming: null, tuitionOptionKey: null }), ...answer },
    },
  }));

  const changeNewChild = (index: number, answer: Partial<NewChildAnswer>) => change((current) => ({
    ...current,
    newChildren: current.newChildren.map((entry, at) => (at === index ? { ...entry, ...answer } : entry)),
  }));

  const anyoneComing = answers.newChildren.length > 0
    || who.children.some((child) => answers.children[child.studentEnrollmentId]?.coming === true);
  const plan = plans.find((option) => option.key === answers.paymentOptionKey);

  return (
    <>
      {/* Held still while a submission is on its way. */}
      <fieldset disabled={submitting} className="min-w-0 space-y-6">
        {who.children.map((child) => (
          <ChildQuestion
            key={child.studentEnrollmentId}
            child={child}
            answer={answers.children[child.studentEnrollmentId]}
            programs={programs}
            onChange={(answer) => changeChild(child.studentEnrollmentId, answer)}
          />
        ))}

        {who.allowNewChildren ? (
          <Section>
            <div className="space-y-1">
              <h2 className="text-lg font-semibold">Adding a child?</h2>
              <p className="text-sm text-muted-foreground">
                Add a brother or sister who isn't listed. The school reviews each new child.
              </p>
            </div>
            {answers.newChildren.map((entry, index) => (
              <NewChildFields
                // Entries have no id until the school adds them, so they're kept by position.
                key={index}
                index={index}
                entry={entry}
                grades={who.grades}
                programs={programs}
                onChange={(answer) => changeNewChild(index, answer)}
                onRemove={() => change((current) => ({
                  ...current,
                  newChildren: current.newChildren.filter((_, at) => at !== index),
                }))}
              />
            ))}
            <Button
              type="button"
              variant="outline"
              className="w-full md:w-auto"
              onClick={() => change((current) => ({ ...current, newChildren: [...current.newChildren, { ...NEW_CHILD }] }))}
            >
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              Add a child
            </Button>
          </Section>
        ) : null}

        {anyoneComing && plans.length > 0 ? (
          <Section>
            <Label htmlFor="who-plan" className="text-lg font-semibold">Payment plan</Label>
            <select
              id="who-plan"
              className={selectClass}
              value={answers.paymentOptionKey ?? ''}
              onChange={(event) => change((current) => ({ ...current, paymentOptionKey: event.target.value || null }))}
            >
              <option value="">Choose a payment plan</option>
              {plans.map((option) => <option key={option.key} value={option.key}>{option.name}</option>)}
            </select>
            {plan?.description ? <p className="text-sm text-muted-foreground">{plan.description}</p> : null}
            <p className="text-sm text-muted-foreground">You can change your plan until you sign.</p>
            {who.aidRequests ? <p className="text-sm text-muted-foreground">Prices are before any financial aid.</p> : null}
          </Section>
        ) : null}

        {anyoneComing && who.aidRequests ? (
          <Section>
            <div className="flex items-start gap-3">
              <Checkbox
                id="who-aid"
                className="mt-0.5 h-5 w-5"
                checked={answers.aidRequested}
                onCheckedChange={(checked) => change((current) => ({ ...current, aidRequested: checked === true }))}
              />
              <Label htmlFor="who-aid" className="text-base font-normal leading-snug">
                We're seeking financial aid
              </Label>
            </div>
          </Section>
        ) : null}
      </fieldset>

      <StepFooter errors={errors} saveState={saveState} submitting={submitting} label="Submit" onSubmit={handleSubmit} />
    </>
  );
}

function ChildQuestion({ child, answer, programs, onChange }: {
  child: Child;
  answer: ChildAnswer | undefined;
  programs: PriceOption[];
  onChange: (answer: Partial<ChildAnswer>) => void;
}) {
  const { yes, no } = comingWords(child.returning);
  const id = `who-${child.studentEnrollmentId}`;
  const value = answer?.coming === true ? 'yes' : answer?.coming === false ? 'no' : '';

  return (
    <Section>
      <div>
        <h2 id={`${id}-name`} className="text-lg font-semibold">{child.name}</h2>
        {child.grade ? <p className="text-sm text-muted-foreground">{gradeLabel(child.grade)}</p> : null}
      </div>
      <RadioGroup
        aria-labelledby={`${id}-name`}
        value={value}
        onValueChange={(picked) => onChange({ coming: picked === 'yes' })}
        className="grid gap-2 md:grid-cols-2"
      >
        <Choice id={`${id}-yes`} value="yes" label={yes} />
        <Choice id={`${id}-no`} value="no" label={no} />
      </RadioGroup>
      {answer?.coming === true && programs.length > 0 ? (
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-program`}>
            Program<span className="sr-only"> for {child.name}</span>
          </Label>
          <ProgramSelect
            id={`${id}-program`}
            value={answer.tuitionOptionKey}
            programs={programs}
            onChange={(tuitionOptionKey) => onChange({ tuitionOptionKey })}
          />
        </div>
      ) : null}
    </Section>
  );
}

function NewChildFields({ index, entry, grades, programs, onChange, onRemove }: {
  index: number;
  entry: NewChildAnswer;
  grades: string[];
  programs: PriceOption[];
  onChange: (answer: Partial<NewChildAnswer>) => void;
  onRemove: () => void;
}) {
  const id = `who-new-${index}`;
  const name = newChildName(entry);
  // One the school already added keeps its details; only the program can change.
  const added = !!entry.studentEnrollmentId;

  return (
    <fieldset className="min-w-0 space-y-3 rounded-md border p-3">
      <legend className="px-1 text-sm font-medium">{name || 'New child'}</legend>
      {added ? (
        entry.gradeLevel ? <p className="text-sm text-muted-foreground">{gradeLabel(entry.gradeLevel)}</p> : null
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          <Field id={`${id}-first-name`} label="First name">
            <Input id={`${id}-first-name`} autoComplete="off" value={entry.firstName} onChange={(event) => onChange({ firstName: event.target.value })} />
          </Field>
          <Field id={`${id}-last-name`} label="Last name">
            <Input id={`${id}-last-name`} autoComplete="off" value={entry.lastName} onChange={(event) => onChange({ lastName: event.target.value })} />
          </Field>
          <Field id={`${id}-birth-date`} label="Birth date">
            <Input
              id={`${id}-birth-date`}
              type="date"
              className="min-w-0 max-w-full"
              value={entry.dateOfBirth}
              onChange={(event) => onChange({ dateOfBirth: event.target.value })}
            />
          </Field>
          <Field id={`${id}-grade`} label="Grade">
            {grades.length > 0 ? (
              <select
                id={`${id}-grade`}
                className={selectClass}
                value={entry.gradeLevel}
                onChange={(event) => onChange({ gradeLevel: event.target.value })}
              >
                <option value="">Choose a grade</option>
                {grades.map((grade) => <option key={grade} value={grade}>{grade}</option>)}
              </select>
            ) : (
              <Input id={`${id}-grade`} value={entry.gradeLevel} onChange={(event) => onChange({ gradeLevel: event.target.value })} />
            )}
          </Field>
        </div>
      )}
      {programs.length > 0 ? (
        <Field id={`${id}-program`} label="Program">
          <ProgramSelect
            id={`${id}-program`}
            value={entry.tuitionOptionKey}
            programs={programs}
            onChange={(tuitionOptionKey) => onChange({ tuitionOptionKey })}
          />
        </Field>
      ) : null}
      {added ? null : (
        <Button type="button" variant="ghost" onClick={onRemove}>
          Remove<span className="sr-only"> {name || 'this child'}</span>
        </Button>
      )}
    </fieldset>
  );
}

function ProgramSelect({ id, value, programs, onChange }: {
  id: string;
  value: string | null;
  programs: PriceOption[];
  onChange: (key: string | null) => void;
}) {
  const chosen = programs.find((program) => program.key === value);
  return (
    <>
      <select id={id} className={selectClass} value={value ?? ''} onChange={(event) => onChange(event.target.value || null)}>
        <option value="">Choose a program</option>
        {programs.map((program) => <option key={program.key} value={program.key}>{programLabel(program)}</option>)}
      </select>
      {chosen?.description ? <p className="text-sm text-muted-foreground">{chosen.description}</p> : null}
    </>
  );
}

/** What was answered, for a done step or the office looking on. */
function WhoSummary({ who, answers, programs, plans }: {
  who: WhoContext;
  answers: WhoAnswers;
  programs: PriceOption[];
  plans: PriceOption[];
}) {
  const anyoneComing = answers.newChildren.length > 0
    || who.children.some((child) => answers.children[child.studentEnrollmentId]?.coming === true);
  const { yes } = comingWords(false);

  return (
    <Section>
      <dl className="divide-y">
        {who.children.map((child) => (
          <Row key={child.studentEnrollmentId} label={child.name}>
            {answerWords(child.returning, answers.children[child.studentEnrollmentId], programs)}
          </Row>
        ))}
        {answers.newChildren.map((entry, index) => {
          const program = optionName(programs, entry.tuitionOptionKey);
          return (
            <Row key={entry.studentEnrollmentId ?? index} label={newChildName(entry) || 'New child'}>
              {program ? `${yes} · ${program}` : yes}
            </Row>
          );
        })}
        {anyoneComing || answers.paymentOptionKey ? (
          <Row label="Payment plan">{optionName(plans, answers.paymentOptionKey) ?? 'Not chosen yet'}</Row>
        ) : null}
        {who.aidRequests || answers.aidRequested ? (
          <Row label="Seeking financial aid">{answers.aidRequested ? 'Yes' : 'No'}</Row>
        ) : null}
      </dl>
    </Section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 py-2 first:pt-0 last:pb-0 md:flex-row md:items-baseline md:justify-between md:gap-4">
      <dt className="min-w-0 break-words font-medium">{label}</dt>
      <dd className="min-w-0 break-words text-muted-foreground md:text-right">{children}</dd>
    </div>
  );
}
